import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { EngineView, FrameDebuggerCapture, FrameDebuggerEvent, FrameDebuggerPreview } from '../../engine/prism.js';
import { formatCount } from '../../lib/profiler-model.js';
import { useCommands } from '../../lib/shortcuts/index.js';
import { EmptyState, IconButton, SelectField } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { resolveView, type ProfilerTarget } from './profiler-store.js';
import './profiler.css';

/**
 * Frame Debugger (Unity's Frame Debugger): Enable freezes the profiled view
 * (play mode pauses) and captures its frame as an ordered list of passes and
 * draw calls. Stepping to an event replays the frame up to it and shows the
 * render target as it was right after that event, with the event's details.
 */

interface PassNode {
  pass: FrameDebuggerEvent;
  draws: FrameDebuggerEvent[];
}

const TARGETS: { value: ProfilerTarget; label: string }[] = [
  { value: 'auto', label: 'Auto (Play when running)' },
  { value: 'play', label: 'Play view' },
  { value: 'scene', label: 'Scene view' },
];

const CHANNELS = ['r', 'g', 'b', 'a'] as const;

function tree(events: readonly FrameDebuggerEvent[]): PassNode[] {
  const out: PassNode[] = [];
  for (const e of events) {
    if (e.kind === 'pass') out.push({ pass: e, draws: [] });
    else out[out.length - 1]?.draws.push(e);
  }
  return out;
}

function decode(base64: string): Uint8ClampedArray<ArrayBuffer> {
  const bin = atob(base64);
  const out = new Uint8ClampedArray(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function drawLabel(e: FrameDebuggerEvent): string {
  if (e.kind === 'pass') return e.name;
  const what = e.mesh?.name ?? e.mesh?.handle;
  return what ? `${e.name} · ${what.split('/').pop()}` : e.name;
}

export function FrameDebuggerPanel() {
  const [target, setTarget] = useState<ProfilerTarget>('auto');
  const [view, setView] = useState<EngineView | null>(null);
  const [capture, setCapture] = useState<FrameDebuggerCapture | null>(null);
  const [index, setIndex] = useState(0);
  const [preview, setPreview] = useState<FrameDebuggerPreview | null>(null);
  const [depth, setDepth] = useState(false);
  const [channels, setChannels] = useState('rgb');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const request = useRef(0);

  const events = useMemo(() => capture?.events ?? [], [capture]);
  const passes = useMemo(() => tree(events), [events]);
  const event = events[index] ?? null;
  const passOf = event ? events[event.pass] ?? null : null;
  const enabled = capture !== null;

  const disable = useCallback(async () => {
    if (view?.alive) await view.frameDebugger({ op: 'disable' });
    setCapture(null);
    setPreview(null);
    setView(null);
  }, [view]);

  const enable = useCallback(async () => {
    const found = resolveView(target);
    if (!found) {
      setError('No Scene or Play view is open.');
      return;
    }
    if (!found.view.supportsFrameDebugger) {
      setError('This engine build has no frame debugger — rebuild the engine (pnpm engine:build).');
      return;
    }
    setBusy(true);
    const result = await found.view.frameDebugger<FrameDebuggerCapture>({ op: 'enable' });
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'Capture failed');
      return;
    }
    setError(null);
    setView(found.view);
    setCapture(result);
    setIndex(Math.max(0, (result.events?.length ?? 1) - 1));
  }, [target]);

  const recapture = useCallback(async () => {
    if (!view?.alive) return;
    const result = await view.frameDebugger<FrameDebuggerCapture>({ op: 'capture' });
    if (!result.ok) return setError(result.error ?? 'Capture failed');
    setCapture(result);
    setIndex((i) => Math.min(i, Math.max(0, (result.events?.length ?? 1) - 1)));
  }, [view]);

  // Leaving the panel (or the view going away) resumes the game.
  useEffect(
    () => () => {
      if (view?.alive) void view.frameDebugger({ op: 'disable' });
    },
    [view],
  );
  useEffect(() => {
    if (!view) return;
    const id = setInterval(() => {
      if (!view.alive) {
        setCapture(null);
        setPreview(null);
        setView(null);
        setError('The debugged view was closed.');
      }
    }, 500);
    return () => clearInterval(id);
  }, [view]);

  // Replay up to the selected event (latest request wins).
  useEffect(() => {
    if (!view?.alive || !event) return;
    const id = ++request.current;
    const timer = setTimeout(async () => {
      setBusy(true);
      const result = await view.frameDebugger<FrameDebuggerPreview>({
        op: 'event',
        index: event.index,
        view: depth ? 'depth' : 'color',
        channels,
        maxSize: 1024,
      });
      if (id !== request.current) return;
      setBusy(false);
      setPreview(result);
    }, 40);
    return () => clearTimeout(timer);
  }, [view, event, depth, channels]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !preview?.ok || !preview.rgba || !preview.width || !preview.height) return;
    canvas.width = preview.width;
    canvas.height = preview.height;
    const data = decode(preview.rgba);
    if (data.length !== preview.width * preview.height * 4) return;
    canvas.getContext('2d')?.putImageData(new ImageData(data, preview.width, preview.height), 0, 0);
  }, [preview]);

  useEffect(() => {
    listRef.current?.querySelector('.is-selected')?.scrollIntoView({ block: 'nearest' });
  }, [index]);

  const step = (delta: number) => setIndex((i) => Math.min(Math.max(0, i + delta), Math.max(0, events.length - 1)));

  useCommands(
    [
      { id: 'frame-debugger.toggle', label: 'Frame Debugger: Enable / Disable', category: 'Frame Debugger', icon: 'frames', run: () => void (enabled ? disable() : enable()) },
      { id: 'frame-debugger.prev', label: 'Frame Debugger: Previous Event', category: 'Frame Debugger', icon: 'chevronUp', repeat: true, enabled: () => enabled, run: () => step(-1) },
      { id: 'frame-debugger.next', label: 'Frame Debugger: Next Event', category: 'Frame Debugger', icon: 'chevronDown', repeat: true, enabled: () => enabled, run: () => step(1) },
    ],
    [enabled, enable, disable, events.length],
  );

  const hasDepth = Boolean(passOf?.target?.depth);
  const hasColor = Boolean(passOf?.target?.color);

  return (
    <div className="fd-panel">
      <div className="prof-toolbar">
        <button type="button" className={`fd-enable${enabled ? ' is-on' : ''}`} disabled={busy && !enabled} onClick={() => void (enabled ? disable() : enable())}>
          <Icon name="frames" size={14} />
          {enabled ? 'Disable' : 'Enable'}
        </button>
        <div className="prof-target">
          <SelectField value={target} options={TARGETS} onChange={(v) => setTarget(v as ProfilerTarget)} />
        </div>
        <IconButton icon="refresh" title="Capture again" disabled={!enabled} onClick={() => void recapture()} />
        <span className="prof-spacer" />
        {enabled && (
          <span className="prof-status muted">
            {events.length} events · {passes.length} passes{capture?.paused ? ' · game paused' : ''}
          </span>
        )}
      </div>
      {error && <div className="prof-error">{error}</div>}
      {!enabled ? (
        <EmptyState icon="frames" title="Frame Debugger">
          Enable to freeze the {target === 'scene' ? 'Scene' : target === 'play' ? 'Play' : 'Play (or Scene)'} view and step through its frame draw call by draw call. Play mode is paused while the debugger is on.
        </EmptyState>
      ) : (
        <div className="fd-body">
          <div className="fd-left">
            <div className="fd-stepper">
              <IconButton icon="chevronUp" title="Previous event" disabled={index <= 0} onClick={() => step(-1)} />
              <input
                type="range"
                min={0}
                max={Math.max(0, events.length - 1)}
                value={index}
                onChange={(e) => setIndex(Number(e.target.value))}
                aria-label="Event"
              />
              <IconButton icon="chevronDown" title="Next event" disabled={index >= events.length - 1} onClick={() => step(1)} />
              <span className="fd-count">
                {index + 1}/{events.length}
              </span>
            </div>
            <div
              className="fd-tree"
              ref={listRef}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' || e.key === 'ArrowRight') step(1);
                else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') step(-1);
                else return;
                e.preventDefault();
              }}
            >
              {passes.map((node) => {
                const open = !collapsed.has(node.pass.index);
                let group: string | undefined;
                return (
                  <div key={node.pass.index} className="fd-pass">
                    <button
                      type="button"
                      className={`fd-row fd-pass-row${index === node.pass.index ? ' is-selected' : ''}${index < node.pass.index ? ' is-after' : ''}`}
                      onClick={() => setIndex(node.pass.index)}
                    >
                      <span
                        className="fd-twisty"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCollapsed((c) => {
                            const next = new Set(c);
                            if (next.has(node.pass.index)) next.delete(node.pass.index);
                            else next.add(node.pass.index);
                            return next;
                          });
                        }}
                      >
                        <Icon name={open ? 'chevronDown' : 'chevronRight'} size={11} />
                      </span>
                      <span className="fd-label">{node.pass.name}</span>
                      <span className="fd-meta">{node.draws.length}</span>
                    </button>
                    {open &&
                      node.draws.map((d) => {
                        const header = d.group && d.group !== group ? d.group : null;
                        group = d.group;
                        return (
                          <div key={d.index}>
                            {header && <div className="fd-group">{header}</div>}
                            <button
                              type="button"
                              className={`fd-row fd-draw-row${index === d.index ? ' is-selected' : ''}${index < d.index ? ' is-after' : ''}`}
                              title={drawLabel(d)}
                              onClick={() => setIndex(d.index)}
                            >
                              <span className="fd-label">{drawLabel(d)}</span>
                              {d.instances !== undefined && d.instances > 1 && <span className="fd-meta">×{d.instances}</span>}
                            </button>
                          </div>
                        );
                      })}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="fd-right">
            <div className="fd-preview-bar">
              <span className="fd-pass-name">{preview?.pass ?? passOf?.name ?? ''}</span>
              {hasColor && hasDepth && (
                <div className="fd-seg">
                  <button type="button" className={!depth ? 'is-on' : ''} onClick={() => setDepth(false)}>
                    Color
                  </button>
                  <button type="button" className={depth ? 'is-on' : ''} onClick={() => setDepth(true)}>
                    Depth
                  </button>
                </div>
              )}
              {!(depth || (!hasColor && hasDepth)) && (
                <div className="fd-seg">
                  {CHANNELS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={channels.includes(c) ? 'is-on' : ''}
                      title={`Show the ${c.toUpperCase()} channel`}
                      onClick={() => setChannels((cur) => (cur.includes(c) ? cur.replace(c, '') || 'rgb' : `${cur}${c}`))}
                    >
                      {c.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}
              {preview?.source && (
                <span className="muted fd-format">
                  {preview.source.width}×{preview.source.height} {preview.source.format}
                </span>
              )}
              {busy && <span className="muted">Rendering…</span>}
            </div>
            <div className="fd-preview">
              {preview && !preview.ok ? (
                <span className="muted">{preview.error}</span>
              ) : (
                <canvas ref={canvasRef} className="fd-canvas" />
              )}
            </div>
            {event && <EventDetails event={event} pass={passOf} />}
          </div>
        </div>
      )}
    </div>
  );
}

function EventDetails({ event, pass }: { event: FrameDebuggerEvent; pass: FrameDebuggerEvent | null }) {
  const rows: [string, string][] = [];
  const add = (k: string, v: string | number | null | undefined) => {
    if (v !== undefined && v !== null && v !== '') rows.push([k, String(v)]);
  };
  add('Event', `#${event.index}`);
  add('Pass', pass?.name);
  add('Camera', event.camera);
  add('Group', event.group);
  if (event.kind === 'pass') {
    const t = event.target;
    if (t) add('Target', `${t.width}×${t.height} ${t.format}${t.color && t.depth ? ' (+ depth)' : t.depth ? ' (depth)' : ''}`);
  } else {
    add('Mesh', event.mesh ? `${event.mesh.name ?? '(runtime mesh)'} [${event.mesh.handle}]` : null);
    add('Material', event.material ? `${event.material.name ?? '(runtime material)'} [${event.material.handle}]` : null);
    add('Shader', event.shader);
    add('Pipeline', event.pipeline);
    add('Vertices', event.vertices !== undefined ? formatCount(event.vertices) : null);
    add('Indices', event.indices ? `${formatCount(event.indices)} (${formatCount(event.indices / 3)} tris)` : null);
    add('Instances', event.instances);
    add('Blend', event.blend);
    add('Cull', event.cull);
    for (const [k, v] of Object.entries(event.params ?? {})) add(k, v);
    if (event.entities?.length) {
      add(
        'Entities',
        event.entities.map((e) => e.part ?? e.name ?? e.stableId ?? `#${e.id}`).join(', ') + (event.instances && event.instances > event.entities.length ? ', …' : ''),
      );
    }
  }
  return (
    <div className="fd-details">
      <div className="fd-details-title">{drawLabel(event)}</div>
      <table>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}>
              <th>{k}</th>
              <td>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
